import { ArrowRightOutlined } from '@ant-design/icons'
import { Button, Checkbox, CheckboxChangeEvent, Dropdown, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { FC, useState } from 'react'
import styled from 'styled-components'
import connect from '../../../../../../../helpers/api/connect'
import useGetDigitalDistributionMachines from '../../../../../../../hooks/api/requests/use-get-digital-distribution-machines'
import useGetFlexographyMachines from '../../../../../../../hooks/api/requests/use-get-flexography-machines'
import { CreatedPrintJobsTab } from '../../types'
import useErrorModal from '../../../../../../../hooks/api/use-error-modal'
import useSuccessModal from '../../../../../../../hooks/api/use-success-modal'
import { userStorage } from '../../../../../../../services/user-storage'
import {
    printJobsApi,
    useUpdatePrintJobMutation,
} from '../../../../../../../store/api/print-jobs-api'
import {
    useCreatePrintQueueEntryMutation,
    useLazyGetPrintQueueEntriesQuery,
} from '../../../../../../../store/api/print-queue-entries-api'
import { useAppDispatch } from '../../../../../../../store/store'
import Machine from '../../../../../../../types/api/machine'
import PrintJob, { JobStatuses } from '../../../../../../../types/api/print-job'
import { Zones } from '../../../../../../../types/api/print-queue-entry'

interface AddToMachineQueueProps {
    printJob: PrintJob
    tab: CreatedPrintJobsTab
}

const OrderedCheckbox = styled(Checkbox)<{ $order?: number }>`
    .ant-checkbox-inner {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .ant-checkbox-checked .ant-checkbox-inner::after {
        display: none;
    }

    .ant-checkbox-checked .ant-checkbox-inner::before {
        content: '${({ $order }) => ($order ? String($order) : '')}';
        color: #fff;
        font-size: 10px;
        font-weight: 600;
        line-height: 1;
    }
`

const AddToMachineQueue: FC<AddToMachineQueueProps> = ({ printJob, tab }) => {
    const dispatch = useAppDispatch()
    const { data: flexoMachinesData } = useGetFlexographyMachines()
    const { data: digitalMachinesData } = useGetDigitalDistributionMachines()
    const data = tab === 'flexo' ? flexoMachinesData : digitalMachinesData
    const [updatePrintJob, updatePrintJobState] = useUpdatePrintJobMutation()
    const [createPrintQueueEntry, createPrintQueueEntryState] = useCreatePrintQueueEntryMutation()
    const [getPrintQueueEntries, getPrintQueueEntriesState] = useLazyGetPrintQueueEntriesQuery()

    const machines = data?.data || []

    useErrorModal(createPrintQueueEntryState, 'Ошибка создания элемента очереди')
    useSuccessModal(createPrintQueueEntryState, 'Задание добавлено в очередь')

    useErrorModal(updatePrintJobState, 'Ошибка обновления статуса задания печати')
    useSuccessModal(updatePrintJobState, 'Статус задания печати обновлен')

    const [selected, setSelected] = useState<Machine[]>([])
    // TODO: question - как быть, если распределили на машины, но хотят все таки добавить в еще одну очередь станка? Обратится к администратору?
    const addToMachineQueueOnePrintJob = async (machine: Machine, executionOrder: number) => {
        const resultOfGetting = await getPrintQueueEntries({
            filters: { machine: { documentId: { $eq: machine.documentId } } },
            fields: ['sort'],
        })

        if (resultOfGetting.data) {
            // TODO: проставлять сортировку на бэке, выбирая самое большое число
            const list = resultOfGetting.data.data
            const maxSort = list.length > 0 ? Math.max(...list.map(x => x.sort)) : 0

            // TODO: вынести в отдельный роут добавление в очередь, надо менять статус у задания печати одновременно
            const result = await createPrintQueueEntry({
                data: {
                    zone: Zones.Main,
                    print_job: connect(printJob),
                    machine: connect(machine),
                    sort: maxSort + 1, // TODO: выяснить текущую сортировку
                    executionOrder,
                },
            })

            if ('data' in result) {
                const result = await updatePrintJob({
                    docId: printJob.documentId,
                    body: {
                        data: {
                            jobStatus: JobStatuses.Planned,
                            assigned_by: connect(userStorage.get()),
                            assignedAt: dayjs().toISOString(),
                        },
                    },
                })

                if ('data' in result) {
                    dispatch(printJobsApi.util.invalidateTags(['PrintJobs']))
                }
            }
        }
    }

    const onConfirm = async () => {
        await Promise.all(
            selected.map((machine, index) => addToMachineQueueOnePrintJob(machine, index + 1))
        )
    }
    const getMachineOrder = (machine: Machine) => {
        const index = selected.findIndex(x => x.documentId === machine.documentId)
        if (index === -1) {
            return undefined
        }

        return index + 1
    }

    const onCheckboxChange = (e: CheckboxChangeEvent, machine: Machine) => {
        setSelected(prev =>
            e.target.checked
                ? [...prev, machine]
                : prev.filter(v => v.documentId !== machine.documentId)
        )
    }

    const isOnlyGfSelected =
        selected.length > 0 && selected.every(machine => machine.machine_brand?.name === 'GF')
    const isPlaceDisabled = selected.length === 0 || isOnlyGfSelected

    useErrorModal(updatePrintJobState, 'Ошибка обновления статуса задания печати')
    useErrorModal(createPrintQueueEntryState, 'Ошибка создания элемента очереди')
    useErrorModal(getPrintQueueEntriesState, 'Ошибка получения элементов очереди')

    return (
        <Dropdown
            trigger={['hover']}
            dropdownRender={() => (
                <div
                    style={{
                        background: '#fff',
                        borderRadius: 8,
                        padding: 8,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}
                >
                    {machines
                        .map(x => x)
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(item => (
                            <div key={item.documentId}>
                                <OrderedCheckbox
                                    $order={getMachineOrder(item)}
                                    checked={selected.some(x => x.documentId === item.documentId)}
                                    onChange={e => onCheckboxChange(e, item)}
                                >
                                    {item.name}
                                </OrderedCheckbox>
                            </div>
                        ))}
                    <Tooltip title={isOnlyGfSelected ? 'Нельзя разместить только на GF' : undefined}>
                        <span style={{ display: 'inline-block', marginTop: 12 }}>
                            <Button
                                type={'primary'}
                                icon={<ArrowRightOutlined />}
                                iconPosition={'end'}
                                disabled={isPlaceDisabled}
                                loading={
                                    getPrintQueueEntriesState.isLoading ||
                                    createPrintQueueEntryState.isLoading ||
                                    updatePrintJobState.isLoading
                                }
                                onClick={onConfirm}
                            >
                                Разместить
                            </Button>
                        </span>
                    </Tooltip>
                </div>
            )}
        >
            <Button size={'small'} icon={<ArrowRightOutlined />} type={'primary'} />
        </Dropdown>
    )
}

export default AddToMachineQueue
